import { push } from 'connected-react-router';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';
import { takeLatest, put, all, select, call } from 'redux-saga/effects';
import {
  UPDATE_CART,
  CLEAR_CART,
  GET_CART_SUMMARY,
} from 'containers/Cart/constants';
import {
  BILLING_TYPE_ANOTHER,
  BILLING_TYPE_SHIPPING,
  PAYMENT_PAGE,
  REVIEW_PAGE,
  SETUP_PAGE,
} from 'containers/PrimoCheckoutPage/constants';
import creditCardType from 'credit-card-type';
import { get, omit, omitBy, isNil } from 'lodash';
import { isPurefloDomain } from 'utils/domainHelper';
import { getCartId } from 'containers/Cart/saga';
import { apiCall } from 'utils/swaggerClient';
import { adjustPostalCode } from 'utils/common';
import { getToUrl, isCanadianBrand } from 'utils/translation';
import { completeProductItemsSaga } from 'containers/PrimoProducts/saga';

import {
  selectCartData,
  selectCartId,
  selectCartSecret,
  selectDeliveryData,
} from 'containers/Cart/selectors';
import {
  selectOrderData,
  selectCheckoutCreditCard,
} from 'containers/CheckoutPage/selectors';
import { selectUserEmail } from 'containers/Authentication/selectors';
import {
  throwErrorTest,
  handleError,
  getErrorMessage,
} from 'utils/errorHandling';
import { errorResponseTransform } from 'utils/errorResponseTransform';
import { isSsr } from 'utils/ssrHelper';
import { getServerData } from 'utils/getServerData';
import {
  dataLayerPush,
  dataLayerPushSuccess,
  dataLayerPushFailure,
} from 'utils/tracking';
import * as constants from './constants';
import { updateStorageCreditCard, clearStorageCreditCard } from './utils';
const prepareCreditCardData = (creditCardData) => {
  const { cardNumber } = creditCardData;
  const formattedCardNumber = cardNumber.replace(/[^0-9]/g, '');
  const txKey = !isSsr() ? getServerData('TOKENEX_API_KEY') : '';
  const encryptedCreditCardNumber = TxEncrypt(txKey, formattedCardNumber);
  const cardType = creditCardType(cardNumber);

  return {
    ...omit(creditCardData, 'cardNumber'),
    encryptedCreditCardNumber,
    cardType: cardType[0].type,
    last4: formattedCardNumber.substr(-4, 4),
  };
};

const removeEmptyProperties = (obj) =>
  omitBy(obj, (el) => isNil(el) || el === '');

function* saveLocationForm({ payload }) {
  try {
    throwErrorTest('saveLocationForm');
    const isPureflo = isPurefloDomain();
    const {
      [constants.PRIMARY_CONTACT_FORM]: contactData,
      [constants.DELIVERY_AND_INSTALLATION_LOCATION_FORM]: locationData,
    } = payload;

    const cartId = yield select(selectCartId());
    const secretId = yield select(selectCartSecret());

    const email = yield select(selectUserEmail());

    const formattedContactData = removeEmptyProperties(contactData);
    const formattedPhoneNumber = parsePhoneNumberFromString(
      contactData.phoneNumber,
      'US',
    ).number;
    const formattedLocationData = locationData.map((data) =>
      removeEmptyProperties(data),
    );
    if (isCanadianBrand()) {
      formattedLocationData.map((data) => {
        data.countryCode = 'CA';
        data.stateOrProvinceCode = data.county;
        return data;
      });
    }
    let contactResponse;
    let addressResponse;
    let bestDeliveryDatesResponse;
    let skipGetDeliveries = false;
    const contactRequestBody = {
      secretId,
      email,
      ...formattedContactData,
      phoneNumber: formattedPhoneNumber,
    };

    try {
      contactResponse = yield call(apiCall, {
        operationId: 'carts.createOrUpdateContact',
        parameters: { cartId },
        options: { requestBody: contactRequestBody },
      });
    } catch (error) {
      if (error.status === 400) {
        const parsedErrors = errorResponseTransform(
          get(error, 'response.body.errors', []),
        );

        yield put({
          type: constants.SAVE_LOCATION_FORM_ERROR,
          errors: {
            [constants.PRIMARY_CONTACT_FORM]: parsedErrors,
          },
        });

        return;
      }

      throw error;
    }
    try {
      addressResponse = yield call(apiCall, {
        operationId: 'carts.createOrUpdateAddresses',
        parameters: { cartId },
        options: {
          requestBody: { servicesAddresses: formattedLocationData, secretId },
        },
      });
    } catch (error) {
      addressResponse = [
        {
          ...formattedLocationData[0],
          latitude: null,
          longitude: null,
          waterRoute: null,
          coffeeRoute: null,
          recurringDeliveryDates: [],
        },
      ];
      skipGetDeliveries = true;
    }

    if (!isPureflo && !skipGetDeliveries) {
      try {
        bestDeliveryDatesResponse = yield call(apiCall, {
          operationId: 'carts.getBestDeliveryDatesWithHolidays',
          parameters: {
            cartId,
            fromDate: moment().add(1, 'day').format('YYYY-MM-DD'),
            toDate: moment().add(28, 'days').format('YYYY-MM-DD'),
            serviceType: 'water',
          },
        });
      } catch (error) {
        bestDeliveryDatesResponse = [];
      }
    }

    const response = {
      contact: isPureflo ? contactResponse : contactRequestBody,
      servicesAddresses: isPureflo
        ? addressResponse
        : addressResponse.map((data) => removeEmptyProperties(data)),
      serviceDates: bestDeliveryDatesResponse,
    };

    yield put({ type: UPDATE_CART, payload: response });
    yield put({
      type: constants.SAVE_LOCATION_FORM_SUCCESS,
      payload: response,
    });

    if (payload.drawerToggle) {
      payload.drawerToggle();
    } else if (isPureflo) {
      yield put(push(`/checkout/${constants.BILLING_PAGE}`));
    } else {
      yield put(push(getToUrl(`/checkout/${SETUP_PAGE}`, 'U61')));
    }
  } catch (error) {
    yield handleError(
      'saveLocationForm',
      constants.SAVE_LOCATION_FORM_ERROR,
      getErrorMessage(error),
    );
  }
}

function* savePaymentForm({ payload }) {
  try {
    throwErrorTest('savePaymentForm');
    const isPureflo = isPurefloDomain();
    const {
      [constants.CREDIT_CARD_FORM]: creditCardData,
      [constants.BILLING_ADDRESS_FORM]: billingAddressData,
    } = payload;

    const paymentResponse = yield call(
      updatePaymentBillingAddress,
      removeEmptyProperties(billingAddressData),
    );
    const responsePayload = { billingAddress: paymentResponse };
    const cartId = yield getCartId();

    if (creditCardData) {
      const preparedCreditCard = prepareCreditCardData(creditCardData);
      yield put({
        type: constants.SAVE_CREDIT_CARD,
        payload: preparedCreditCard,
      });

      if (isPureflo) {
        updateStorageCreditCard(preparedCreditCard);
      } else {
        responsePayload.creditCard = preparedCreditCard;
      }
    }

    yield put({ type: UPDATE_CART, payload: responsePayload });
    yield put({
      type: constants.SAVE_PAYMENT_FORM_SUCCESS,
      payload: paymentResponse,
    });

    if (cartId) {
      yield put({ type: GET_CART_SUMMARY, payload: { cartId } });
    }

    if (payload.drawerToggle) {
      payload.drawerToggle();
    } else if (isPureflo) {
      yield put(push(`/checkout/${constants.CONFIRMATION_PAGE}`));
    } else {
      yield put(push(getToUrl(`/checkout/${REVIEW_PAGE}`, 'U62')));
    }
  } catch (error) {
    if (error.status === 400) {
      const paymentErrors = errorResponseTransform(
        get(error, 'response.body.errors', []),
      );

      yield put({
        type: constants.SAVE_LOCATION_FORM_ERROR,
        errors: {
          [constants.BILLING_ADDRESS_FORM]: paymentErrors,
        },
      });

      return;
    }

    yield handleError(
      'savePaymentForm',
      constants.SAVE_PAYMENT_FORM_ERROR,
      getErrorMessage(error),
    );
  }
}

function* saveDeliverySetupForm({ payload }) {
  try {
    throwErrorTest('saveDeliverySetupForm');
    yield put({ type: UPDATE_CART, payload: { deliverySetup: payload } });
    yield put({ type: constants.SAVE_DELIVERY_SETUP_FORM_SUCCESS });
    yield put(push(getToUrl(`/checkout/${PAYMENT_PAGE}`, 'U27')));
  } catch (error) {
    yield handleError(
      'saveDeliverySetupForm',
      constants.SAVE_DELIVERY_SETUP_FORM_ERROR,
      getErrorMessage(error),
    );
  }
}

function* updatePaymentBillingAddress(billingAddressData) {
  const isPureflo = isPurefloDomain();
  const cartId = yield select(selectCartId());
  const { billingAddress } = yield select(selectCartData());
  let paymentResponse = { billingType: billingAddressData.billingType };

  if (!isPureflo && billingAddressData.billingType === BILLING_TYPE_SHIPPING) {
    if (billingAddress.billingType === BILLING_TYPE_ANOTHER) {
      yield call(apiCall, {
        operationId: 'billingAddress.deleteBillingAddress',
        parameters: { cartId },
      });
    }
  } else {
    const responseObj = yield call(apiCall, {
      operationId: 'billingAddress.upsertBillingAddress',
      parameters: { cartId },
      options: { requestBody: billingAddressData },
    });

    paymentResponse = isPureflo ? responseObj : billingAddressData;
  }

  return paymentResponse;
}

function* placeOrder({ payload }) {
  try {
    throwErrorTest('placeOrder');
    const isPureflo = isPurefloDomain();
    const cartId = yield select(selectCartId());
    const cartData = yield select(selectCartData());
    const creditCard = payload.creditCardData
      ? prepareCreditCardData(payload.creditCardData)
      : yield select(selectCheckoutCreditCard());
    const { last4, ...creditCardRequest } = creditCard;

    const serviceZipCode =
      cartData[constants.DELIVERY_AND_INSTALLATION_LOCATION_FORM][0].postalCode;

    const requestBody = {
      ...creditCardRequest,
      ...payload.deliverySetup,
      serviceZipCode,
      captchaToken: payload.captchaToken,
    };

    const placeOrderResponse = yield call(apiCall, {
      operationId: 'orders.createOrder',
      parameters: { cartId },
      options: { requestBody },
    });

    if (!isPureflo) {
      placeOrderResponse.delivery = cartData[constants.PRIMARY_CONTACT_FORM];
      placeOrderResponse.deliverySetup =
        cartData[constants.DELIVERY_SETUP_FORM];
      placeOrderResponse.billing = cartData[constants.BILLING_ADDRESS_FORM];
      placeOrderResponse.items = yield call(
        completeProductItemsSaga,
        placeOrderResponse.items,
      );
    }

    yield put({
      type: constants.PLACE_ORDER_SUCCESS,
      payload: placeOrderResponse,
    });
    if (isPureflo) {
      yield put(
        push(`/checkout/${constants.SUCCESS_PAGE}/${placeOrderResponse.id}`),
      );
    } else {
      yield put(
        push(getToUrl(`/checkout/${constants.CONFIRMATION_PAGE}`, 'U28')),
      );
    }

    const cartDeliveryData = yield select(selectDeliveryData());
    const postalCode =
      cartDeliveryData && adjustPostalCode(cartDeliveryData.postalCode);

    if (postalCode) {
      const zipInfo = yield apiCall({
        operationId: 'brands.getAcqServiceabilityByZip',
        parameters: {
          postalCode,
        },
      });

      dataLayerPush('Acquisition', {
        division: zipInfo.divisionName,
        region: zipInfo.regionName,
      });
    }

    dataLayerPushSuccess('PUSH_ADD_TO_PLACE_ORDER');

    yield put({ type: CLEAR_CART });

    if (isPureflo) {
      clearStorageCreditCard();
    }
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    // TODO: Getting the below message when we submit an invalid card and the payment drawer is not opened, check with back end team
    // We're sorry you're experiencing an error. Please contact us at 800-261-7524 for immediate assistance. Thank you! object Error: We're sorry you're experiencing an error. Please contact us at 800-261-7524 for immediate assistance. Thank you!
    if (
      errorMessage &&
      (errorMessage.startsWith('Could not authorise payment') ||
        errorMessage.startsWith('Could not authorize payment'))
    ) {
      // This will open the payment drawer
      yield put({
        type: constants.PAYMENT_PROCESS_ERROR,
        errors: [errorMessage],
      });
    } else {
      dataLayerPushFailure('PUSH_ADD_TO_PLACE_ORDER');
      yield handleError(
        'placeOrder',
        constants.PLACE_ORDER_ERROR,
        errorMessage,
      );
    }
  }
}

function* fetchOrder({ payload }) {
  try {
    let order = yield select(selectOrderData());

    if (!order) {
      order = yield call(apiCall, {
        operationId: 'orders.getOrder',
        parameters: { orderId: payload.orderId },
      });
    }

    order.items = yield call(completeProductItemsSaga, order.items);

    yield put({ type: constants.FETCH_ORDER_SUCCESS, payload: order });
  } catch (error) {
    yield put({ type: constants.FETCH_ORDER_ERROR, payload: error });
    yield put(push(getToUrl('/cart', 'U29')));
  }
}

function* fetchCityCounty({ payload: { postalCode } }) {
  postalCode = adjustPostalCode(postalCode);
  try {
    const cityCountyList = yield call(apiCall, {
      operationId: 'carts.getCityCountyComboByZip',
      parameters: { postalCode },
    });
    yield put({
      type: constants.FETCH_CITY_COUNTY_SUCCESS,
      payload: cityCountyList,
    });
  } catch (error) {
    yield put({ type: constants.FETCH_CITY_COUNTY_ERROR, payload: error });
  }
}

function* createPassword({ payload }) {
  const { cartId, password } = payload;
  try {
    throwErrorTest('createPassword');
    yield call(apiCall, {
      operationId: 'orders.createPassword',
      parameters: {
        cartId,
        clientApp: 'cx',
      },
      options: { requestBody: { password } },
    });
    yield put({ type: constants.CREATE_PASSWORD_SUCCESS });
  } catch (error) {
    yield put({
      type: constants.CREATE_PASSWORD_ERROR,
      payload: getErrorMessage(error),
    });
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(constants.SAVE_LOCATION_FORM, saveLocationForm),
    takeLatest(constants.SAVE_PAYMENT_FORM, savePaymentForm),
    takeLatest(constants.SAVE_DELIVERY_SETUP_FORM, saveDeliverySetupForm),
    takeLatest(constants.PLACE_ORDER, placeOrder),
    takeLatest(constants.FETCH_ORDER, fetchOrder),
    takeLatest(constants.FETCH_CITY_COUNTY, fetchCityCounty),
    takeLatest(constants.CREATE_PASSWORD, createPassword),
  ]);
}
